import { memo, useCallback, useState } from 'react';
import { ColorSwatch, ColorPicker, Popover, Tooltip } from '@mantine/core';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import styles from './SwatchColorPicker.module.css';

const CHECK_ICON_SIZE = 12;
const PLUS_ICON_SIZE = 14;

interface SwatchColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  swatches: readonly string[];
  allowCustomColor?: boolean;
  swatchSize?: number;
  className?: string;
}

export const SwatchColorPicker = memo(function SwatchColorPicker({
  value,
  onChange,
  swatches,
  allowCustomColor = false,
  swatchSize = 28,
  className,
}: SwatchColorPickerProps) {
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const handleColorClick = useCallback(
    (color: string) => {
      onChange(color);
      setShowCustomPicker(false);
    },
    [onChange]
  );

  const handleToggleCustom = useCallback(() => {
    setShowCustomPicker((prev) => !prev);
  }, []);

  const isCustomColor =
    allowCustomColor && !swatches.includes(value) && value !== '';

  return (
    <div className={className}>
      <div className={styles.grid}>
        {swatches.map((color) => (
          <Tooltip key={color} label={color} position="top" withArrow>
            <ColorSwatch
              color={color}
              size={swatchSize}
              onClick={() => handleColorClick(color)}
              className={styles.swatch}
            >
              {value === color && (
                <IconCheck
                  size={CHECK_ICON_SIZE}
                  className={styles.checkIcon}
                />
              )}
            </ColorSwatch>
          </Tooltip>
        ))}

        {allowCustomColor && (
          <Popover
            opened={showCustomPicker}
            onChange={setShowCustomPicker}
            position="bottom-start"
            shadow="md"
            withinPortal
          >
            <Popover.Target>
              <Tooltip label="Custom color" position="top" withArrow>
                <ColorSwatch
                  color={isCustomColor ? value : 'transparent'}
                  size={swatchSize}
                  onClick={handleToggleCustom}
                  className={`${styles.swatch} ${styles.customSwatch}`}
                >
                  {isCustomColor ? (
                    <IconCheck
                      size={CHECK_ICON_SIZE}
                      className={styles.checkIcon}
                    />
                  ) : (
                    <IconPlus
                      size={PLUS_ICON_SIZE}
                      className={styles.plusIcon}
                    />
                  )}
                </ColorSwatch>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown className={styles.pickerDropdown}>
              <ColorPicker
                value={value}
                onChange={onChange}
                format="hex"
                size="sm"
                swatches={swatches as string[]}
              />
            </Popover.Dropdown>
          </Popover>
        )}
      </div>
    </div>
  );
});
